// Помощните линии при местене: елементът „залепва" за центъра или ръба
// (на плота, без столовете) на друг елемент, когато е на няколко пиксела от
// тях, и докато е подравнен, тънка линия минава през двата. Чиста геометрия
// в логически единици — рисуването е в `SchemaCanvas`.

import type { SchemaElement } from "../../../../api/restaurantSchema";
import { elementHalfExtents, snapToGrid } from "../../../../components/restaurant-schema/geometry";

/** Линия в логически единици: вертикална (`x`, от `start` до `end` по y) или хоризонтална. */
export interface AlignmentGuide {
  orientation: "vertical" | "horizontal";
  position: number;
  start: number;
  end: number;
}

interface Box {
  x: number;
  y: number;
  halfWidth: number;
  halfHeight: number;
}

const boxOf = (element: SchemaElement, x = element.x, y = element.y): Box => ({
  x,
  y,
  ...elementHalfExtents(element, false),
});

// Точките по една ос, по които се подравнява: ляв ръб, център, десен ръб.
const anchorsX = (box: Box) => [box.x - box.halfWidth, box.x, box.x + box.halfWidth];
const anchorsY = (box: Box) => [box.y - box.halfHeight, box.y, box.y + box.halfHeight];

// По-малко от това (логически единици) = „на една линия".
const ALIGNED_EPSILON = 0.5;

/**
 * Най-малкото изместване по оста, с което точка на местения елемент застава
 * точно върху точка на друг — ако е до `threshold`; иначе null.
 */
const nearestSnap = (moving: number[], targets: number[], threshold: number) => {
  let best: number | null = null;
  for (const from of moving) {
    for (const to of targets) {
      const delta = to - from;
      if (Math.abs(delta) <= threshold && (best === null || Math.abs(delta) < Math.abs(best))) best = delta;
    }
  }
  return best;
};

// Линиите през всички съседи, с които елементът в `x`/`y` е точно на една
// линия — само по поисканите оси.
const guidesFor = (
  element: SchemaElement,
  x: number,
  y: number,
  neighbours: Box[],
  axes: { vertical: boolean; horizontal: boolean },
): AlignmentGuide[] => {
  const moving = boxOf(element, x, y);
  const guides: AlignmentGuide[] = [];
  const addGuide = (orientation: AlignmentGuide["orientation"], position: number, spanBoxes: Box[]) => {
    const isVertical = orientation === "vertical";
    const start = Math.min(...spanBoxes.map((box) => (isVertical ? box.y - box.halfHeight : box.x - box.halfWidth)));
    const end = Math.max(...spanBoxes.map((box) => (isVertical ? box.y + box.halfHeight : box.x + box.halfWidth)));
    guides.push({ orientation, position, start, end });
  };
  const alignedAlong = (movingAnchors: number[], anchorsOf: (box: Box) => number[], orientation: AlignmentGuide["orientation"]) => {
    for (const position of movingAnchors) {
      const aligned = neighbours.filter((box) =>
        anchorsOf(box).some((anchor) => Math.abs(anchor - position) < ALIGNED_EPSILON),
      );
      if (aligned.length > 0) addGuide(orientation, position, [moving, ...aligned]);
    }
  };
  if (axes.vertical) alignedAlong(anchorsX(moving), anchorsX, "vertical");
  if (axes.horizontal) alignedAlong(anchorsY(moving), anchorsY, "horizontal");
  return guides;
};

/**
 * Позицията на местения елемент при `x`/`y` (суровата, под пръста) с
 * подравняването към останалите и линиите, които да се покажат. По оста без
 * подравняване позицията е на решетката (`snapToGrid`). `threshold` е в
 * логически единици (няколко пиксела ÷ мащаба).
 */
export const alignToNeighbours = (
  element: SchemaElement,
  x: number,
  y: number,
  others: SchemaElement[],
  threshold: number,
): { x: number; y: number; guides: AlignmentGuide[] } => {
  const neighbours = others.filter((other) => other.id !== element.id).map((other) => boxOf(other));
  const raw = boxOf(element, x, y);
  const snapX = nearestSnap(anchorsX(raw), neighbours.flatMap(anchorsX), threshold);
  const snapY = nearestSnap(anchorsY(raw), neighbours.flatMap(anchorsY), threshold);
  const snapped = {
    x: snapX === null ? snapToGrid(x) : Math.round(x + snapX),
    y: snapY === null ? snapToGrid(y) : Math.round(y + snapY),
  };
  const guides = guidesFor(element, snapped.x, snapped.y, neighbours, {
    vertical: snapX !== null,
    horizontal: snapY !== null,
  });
  return { ...snapped, guides };
};

/**
 * Линиите за елемент, който е застанал другаде, а не там, където го е сложил
 * `alignToNeighbours` (маса, опряла в съседна маса — `tableCollision.ts`):
 * през всички съседи, с които е точно на една линия, по двете оси.
 */
export const alignmentGuidesAt = (
  element: SchemaElement,
  x: number,
  y: number,
  others: SchemaElement[],
): AlignmentGuide[] =>
  guidesFor(
    element,
    x,
    y,
    others.filter((other) => other.id !== element.id).map((other) => boxOf(other)),
    { vertical: true, horizontal: true },
  );
